import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { InboundSettings } from './inboundApi';

// Settings → Inbounds (docs/INBOUND.md; backend/inbound.py app_settings 'inbound'). Fernando 29.10: each inbound station
// has its own boxes 1–99; the small P&P cart carries 9 boxes (configurable); confirming a shelf by scanning its label
// waits for the section stickers to get a barcode (WH Labels setting); the inbounds@ reader is switched on here once
// WGR's copies arrive.

const input = 'bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm keep-width w-20 text-center';

export default function InboundSettingsCard() {
  const [s, setS] = useState<InboundSettings | null>(null);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    authFetch(`${API}/inbound/settings`).then(r => r.ok ? r.json() : null).then(d => d && setS(d.settings)).catch(() => {});
  }, []);
  if (!s) return <div className="text-gray-500 text-sm">Loading…</div>;

  const change = (patch: Partial<InboundSettings>) => { setS({ ...s, ...patch }); setDirty(true); };
  const num = (v: string) => parseInt(v.replace(/\D/g, ''), 10) || 0;
  const save = async () => {
    const r = await authFetch(`${API}/inbound/settings`, { method: 'PUT', body: JSON.stringify(s) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Could not save'); return; }
    setS(d.settings); setDirty(false); setMsg('✓ Saved'); setTimeout(() => setMsg(''), 2500);
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4 max-w-3xl">
      <div className="text-white text-sm font-medium">Inbounds</div>
      <div className="text-gray-500 text-xs mt-0.5 mb-3">
        Pieces are scanned into numbered grey boxes — one number per style (and shelf), never per shelf, so products are never
        mixed. Every inbound station has its own set of numbers. The shelves stay hidden until replenishing starts; the WMS then
        plans the trips one aisle pair at a time and says in which order to load the cart.
      </div>
      <div className="space-y-2 mb-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          Numbered boxes at one inbound station: 1 to
          <input value={String(s.boxes_per_station)} onChange={e => change({ boxes_per_station: num(e.target.value) })} className={input} /> (1–99)
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          The cart carries
          <input value={String(s.cart_boxes)} onChange={e => change({ cart_boxes: num(e.target.value) })} className={input} /> boxes per trip
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          Manager mode at a station lasts
          <input value={String(s.manager_mode_minutes)} onChange={e => change({ manager_mode_minutes: num(e.target.value) })} className={input} /> minutes
          <span className="text-gray-500 text-xs">a manager's badge once → barcode edits without a PIN per edit, until End or the time is up</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          WGR's supplier orders (step 1 of the booking mail)
          <input value={s.wgr_supplier_orders_url || ''} onChange={e => change({ wgr_supplier_orders_url: e.target.value })}
            className="bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm w-[34rem] max-w-full" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          A shelf stop is confirmed by
          <select value={s.replenish_confirm} onChange={e => change({ replenish_confirm: e.target.value as any })} className="bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm">
            <option value="tap">tapping Done</option>
            <option value="scan">scanning the shelf label's barcode</option>
          </select>
          <span className="text-gray-500 text-xs">scanning needs section labels with a barcode (Tools → WH Labels → Label stock)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          Articles the WMS does not know yet: fetch at most
          <input value={String(s.sync_cap)} onChange={e => change({ sync_cap: num(e.target.value) })} className={input} /> from WGR per import (one call each)
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300 pt-2">
          <input type="checkbox" checked={s.mail_reader} onChange={e => change({ mail_reader: e.target.checked })} />
          Read the supplier-order mails in inbounds@ and make the inbounds by themselves (every 15 minutes, at most 10 mails a pass)
        </label>
        <div className={`flex flex-wrap items-center gap-2 text-sm text-gray-300 ${s.mail_reader ? '' : 'opacity-40'}`}>
          Gmail search
          <input value={s.mail_query} onChange={e => change({ mail_query: e.target.value })} className="bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm flex-1 min-w-[320px]" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={!dirty} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded-lg">Save</button>
        {msg && <span className="text-sm text-gray-400">{msg}</span>}
      </div>
    </div>
  );
}
